import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { GlobalOutlined } from '@ant-design/icons';
import { LANGUAGE_OPTIONS, changeAppLanguage, AppLang } from '../../i18n';
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Badge,
  Button,
  Space,
  Breadcrumb,
  Switch,
  message,
} from 'antd';
import {
  DashboardOutlined,
  ShoppingCartOutlined,
  // CreditCardOutlined, // 发卡入口隐藏后暂未使用
  BankOutlined,
  SettingOutlined,
  UserOutlined,
  LogoutOutlined,
  BellOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HomeOutlined,
  TeamOutlined,
  WalletOutlined,
  SwapOutlined,
  LinkOutlined,
  RollbackOutlined,
  WarningOutlined,
  ApiOutlined,
  BarChartOutlined,
  AppstoreOutlined,
  ShopOutlined,
  ShoppingOutlined,
  SyncOutlined,
  PlusOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { RootState, AppDispatch } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { toggleSidebar, setCurrentPage, setBreadcrumb } from '../../store/slices/uiSlice';
import { fetchUnreadCount } from '../../store/slices/notificationSlice';
import MerchantService, { MerchantSummary } from '../../services/merchant';

const { Header, Sider, Content } = Layout;

const DashboardLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();

  const currentLang = (i18n.language || 'en').split('-')[0];
  const currentLangLabel =
    LANGUAGE_OPTIONS.find((o) => o.code === currentLang)?.label || 'English';
  const languageMenuItems = LANGUAGE_OPTIONS.map((o) => ({
    key: o.code,
    label: o.label,
    onClick: () => changeAppLanguage(o.code as AppLang),
  }));

  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { sidebarCollapsed } = useSelector((state: RootState) => state.ui);
  const { unreadCount } = useSelector((state: RootState) => state.notification);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  // 多店铺切换
  const [stores, setStores] = useState<MerchantSummary[]>([]);
  const [switching, setSwitching] = useState(false);

  const currentMerchantId = (user as any)?.current_merchant_id || (user as any)?.merchant_id;

  const storeLabel = (s: MerchantSummary) =>
    s.business_name || s.name || s.id || t('nav.unnamedStore');

  // 加载店铺列表（后端 /merchant/list 当前可能 500，失败时静默降级为单店铺）
  useEffect(() => {
    let cancelled = false;
    MerchantService.listMerchants()
      .then((list) => {
        if (!cancelled) setStores(list);
      })
      .catch(() => {
        if (!cancelled) setStores([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSwitchStore = async (id: string) => {
    if (!id || id === currentMerchantId || switching) return;
    setSwitching(true);
    try {
      const data: any = await MerchantService.switchMerchant(id);
      const newToken = data?.access_token || data?.token;
      if (newToken) {
        localStorage.setItem('token', newToken);
      }
      message.success(t('nav.switchStoreSuccess'));
      // 重新加载以使用新 token 拉取该店铺数据
      window.location.href = '/dashboard';
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('nav.switchStoreFailed'));
    } finally {
      setSwitching(false);
    }
  };

  // 店铺切换下拉菜单项
  const storeMenuItems = [
    ...stores.map((s) => ({
      key: s.id,
      icon: <ShopOutlined />,
      label: storeLabel(s),
      onClick: () => handleSwitchStore(s.id),
    })),
    ...(stores.length > 0
      ? [{ key: 'store-divider', type: 'divider' as const }]
      : []),
    {
      key: 'add-store',
      icon: <PlusOutlined />,
      label: t('nav.addStore'),
      onClick: () => navigate('/onboarding'),
    },
  ];

  const currentStore = stores.find((s) => s.id === currentMerchantId);
  const currentStoreLabel = currentStore ? storeLabel(currentStore) : t('nav.myStore');

  // 认证检查：未登录时重定向到登录页
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // 菜单配置
  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: t('nav.dashboard'),
      path: '/dashboard',
    },
    {
      key: 'finance',
      icon: <WalletOutlined />,
      label: t('nav.finance'),
      children: [
        {
          key: 'balance',
          icon: <WalletOutlined />,
          label: t('nav.balance'),
          path: '/balance',
        },
        {
          key: 'treasury',
          icon: <SwapOutlined />,
          label: t('nav.treasury'),
          path: '/treasury',
        },
      ],
    },
    {
      key: 'orders',
      icon: <ShoppingCartOutlined />,
      label: t('nav.orders'),
      path: '/orders',
    },
    {
      key: 'catalog-products',
      icon: <ShopOutlined />,
      label: t('nav.products'),
      path: '/catalog/products',
    },
    {
      key: 'customers',
      icon: <TeamOutlined />,
      label: t('nav.customers'),
      path: '/customers',
    },
    {
      key: 'checkout-sessions',
      icon: <ShoppingOutlined />,
      label: t('nav.checkoutSessions'),
      path: '/checkout-sessions',
    },
    {
      key: 'subscriptions',
      icon: <SyncOutlined />,
      label: t('nav.subscriptions'),
      path: '/subscriptions',
    },
    {
      key: 'payments',
      icon: <BankOutlined />,
      label: t('nav.payments'),
      path: '/payments',
    },
    {
      key: 'transactions',
      icon: <SwapOutlined />,
      label: t('nav.transactions'),
      children: [
        {
          key: 'payment-links',
          icon: <LinkOutlined />,
          label: t('nav.paymentLinks'),
          path: '/payment-links',
        },
        {
          key: 'refunds',
          icon: <RollbackOutlined />,
          label: t('nav.refunds'),
          path: '/refunds',
        },
        {
          key: 'disputes',
          icon: <WarningOutlined />,
          label: t('nav.disputes'),
          path: '/disputes',
        },
      ],
    },
    // 虚拟卡（发卡）功能未上线，暂时隐藏入口；页面与服务保留，便于将来恢复。
    // {
    //   key: 'cards',
    //   icon: <CreditCardOutlined />,
    //   label: t('nav.cards'),
    //   path: '/cards',
    // },
    {
      key: 'system',
      icon: <AppstoreOutlined />,
      label: t('nav.system'),
      children: [
        {
          key: 'channels',
          icon: <ApiOutlined />,
          label: t('nav.channels'),
          path: '/channels',
        },
        {
          key: 'notifications',
          icon: <BellOutlined />,
          label: t('nav.notifications'),
          path: '/notifications',
        },
        {
          key: 'reports',
          icon: <BarChartOutlined />,
          label: t('nav.reports'),
          path: '/reports',
        },
      ],
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('nav.settings'),
      path: '/settings',
    },
  ];

  // Fetch unread notification count on mount and refresh every 60 seconds
  useEffect(() => {
    dispatch(fetchUnreadCount());
    const interval = setInterval(() => {
      dispatch(fetchUnreadCount());
    }, 60000); // Refresh every minute

    return () => clearInterval(interval);
  }, [dispatch]);

  // 根据当前路径设置选中的菜单项
  useEffect(() => {
    const currentPath = location.pathname;
    let foundKey: string | null = null;

    // 遍历菜单项，包括子菜单
    for (const item of menuItems) {
      if (item.path && currentPath.startsWith(item.path)) {
        foundKey = item.key;
        break;
      }
      if (item.children) {
        for (const child of item.children) {
          if (child.path && currentPath.startsWith(child.path)) {
            foundKey = child.key;
            break;
          }
        }
      }
      if (foundKey) break;
    }

    if (foundKey) {
      setSelectedKeys([foundKey]);
      dispatch(setCurrentPage(foundKey));
    }
  }, [location.pathname, dispatch]);

  // 设置面包屑
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbItems = [
      { name: t('nav.home'), path: '/dashboard' },
    ];

    if (pathSegments.length > 1) {
      const currentMenuItem = menuItems.find(item =>
        item.path === `/${pathSegments[0]}`
      );
      if (currentMenuItem && currentMenuItem.path) {
        breadcrumbItems.push({ name: currentMenuItem.label, path: currentMenuItem.path });
      }
    }

    dispatch(setBreadcrumb(breadcrumbItems));
  }, [location.pathname, dispatch]);

  const handleMenuClick = (key: string) => {
    // 查找菜单项（包括子菜单）
    let targetItem: any = null;
    for (const item of menuItems) {
      if (item.key === key && item.path) {
        targetItem = item;
        break;
      }
      if (item.children) {
        targetItem = item.children.find((child: any) => child.key === key);
        if (targetItem) break;
      }
    }

    if (targetItem && targetItem.path) {
      navigate(targetItem.path);
    }
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout());
      message.success(t('nav.logoutSuccess'));
      navigate('/login');
    } catch (error) {
      message.error(t('nav.logoutFailed'));
    }
  };

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  // 用户菜单
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('nav.profile'),
      onClick: () => navigate('/settings/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('nav.accountSettings'),
      onClick: () => navigate('/settings'),
    },
    {
      key: 'divider',
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('nav.logout'),
      onClick: handleLogout,
    },
  ];

  return (
    <Layout className="dashboard-layout">
      {/* 侧边栏 */}
      <Sider
        trigger={null}
        collapsible
        collapsed={sidebarCollapsed}
        className="dashboard-sider"
        width={256}
      >
        <div className="logo">
          <div className="logo-icon">★</div>
          {!sidebarCollapsed && (
            <span className="logo-text">
              Fast<span className="logo-accent">Star</span>
            </span>
          )}
        </div>

        <Menu
          theme="light"
          mode="inline"
          selectedKeys={selectedKeys}
          items={menuItems.map(item => {
            if (item.children) {
              return {
                key: item.key,
                icon: item.icon,
                label: item.label,
                children: item.children.map((child: any) => ({
                  key: child.key,
                  icon: child.icon,
                  label: child.label,
                  onClick: () => handleMenuClick(child.key),
                })),
              };
            }
            return {
              key: item.key,
              icon: item.icon,
              label: item.label,
              onClick: () => handleMenuClick(item.key),
            };
          })}
        />
      </Sider>

      <Layout className="dashboard-main">
        {/* 顶部导航 */}
        <Header className="dashboard-header">
          <div className="header-left">
            <Button
              type="text"
              icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={handleToggleSidebar}
              className="sidebar-trigger"
            />

            <Breadcrumb className="header-breadcrumb">
              <Breadcrumb.Item>
                <HomeOutlined />
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t('nav.dashboard')}</Breadcrumb.Item>
            </Breadcrumb>

            {/* 店铺切换 */}
            <Dropdown
              menu={{ items: storeMenuItems, selectedKeys: currentMerchantId ? [currentMerchantId] : [] }}
              placement="bottomLeft"
              trigger={['click']}
            >
              <Button
                type="text"
                loading={switching}
                style={{ marginLeft: 16, display: 'flex', alignItems: 'center' }}
              >
                <ShopOutlined style={{ marginRight: 6 }} />
                <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {currentStoreLabel}
                </span>
                <DownOutlined style={{ marginLeft: 6, fontSize: 12 }} />
              </Button>
            </Dropdown>
          </div>

          <div className="header-right">
            <Space size="middle">
              {/* 语言切换 */}
              <Dropdown
                menu={{ items: languageMenuItems, selectedKeys: [currentLang] }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Button type="text" className="header-action" icon={<GlobalOutlined />}>
                  <span style={{ marginLeft: 4 }}>{currentLangLabel}</span>
                </Button>
              </Dropdown>

              {/* 通知 */}
              <Badge count={unreadCount} size="small">
                <Button
                  type="text"
                  icon={<BellOutlined />}
                  className="header-action"
                  onClick={() => navigate('/notifications')}
                />
              </Badge>

              {/* 用户菜单 */}
              <Dropdown
                menu={{ items: userMenuItems }}
                placement="bottomRight"
                trigger={['click']}
              >
                <div className="user-menu">
                  <Avatar
                    size="small"
                    icon={<UserOutlined />}
                    src={user?.avatar}
                  />
                  <span className="user-name">{user?.first_name} {user?.last_name}</span>
                </div>
              </Dropdown>
            </Space>
          </div>
        </Header>

        {/* 主要内容区域 */}
        <Content className="dashboard-content">
          <div className="content-wrapper">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default DashboardLayout; 